import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { convertHeicIfNeeded } from '../../utils/file';

const OCR_ENDPOINT =
  process.env.REACT_APP_OCR_ENDPOINT ?? 'https://nioverify.cloud.nurseio.com/fileToStandardJson';

const FileUpload = ({
  label,
  required,
  side,
  onFileProcessed,
  handleFailedUpload,
  selectedDocType,
  isUploading,
  setIsUploading,
  uploadedFileName
}) => {
  const [fileName, setFileName] = useState('');
  const [isUploaded, setIsUploaded] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Reset states only when a new file is selected
    setIsUploaded(false);
    setFileName(file.name);
    setIsUploading(true);

    try {
      // Convert file to binary data
      const fileReader = new FileReader();
      fileReader.onload = async () => {
        const binaryData = fileReader.result;

        try {
          const processedFile = await convertHeicIfNeeded({
            binaryData,
            name: file.name
          });
          const ext = processedFile.name.split('.').pop().toLowerCase();
          const contentType =
            ext === 'jpg' || ext === 'jpeg'
              ? 'image/jpeg'
              : file.type || 'application/octet-stream';

          // Send file to OCR API
          const response = await axios.post(OCR_ENDPOINT, processedFile.binaryData, {
            headers: {
              'Content-Type': contentType
            }
          });

          // Pass processed data back to parent component
          if (response.data && response.data.standardized_fields) {
            setIsUploaded(true);
            onFileProcessed(response.data.standardized_fields, side, processedFile);
          } else {
            handleFailedUpload(side, processedFile);
          }

          // Make sure to set isUploading to false on success
          setIsUploading(false);
        } catch (error) {
          handleFailedUpload(side, { binaryData, name: file.name });
          console.error('Error processing file:', error);
          // alert('Error processing file. Please try again.');

          // Notify that processing is complete even if there was an error
          setIsUploading(false);
        }
      };

      fileReader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('Error reading file:', error);
      setIsUploading(false);
      handleFailedUpload(side, {});
      onFileProcessed({}, side);
    }
  };

  const handleClick = () => {
    if (!isUploaded) {
      fileInputRef.current.click();
    }
  };

  const handleUploadAgain = (e) => {
    e.stopPropagation();
    fileInputRef.current.click();
  };

  useEffect(() => {
    setFileName('');
    setIsUploaded(false);
    setIsUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [selectedDocType, setIsUploading]);
  return (
    <div className="form-group">
      <label>
        {label} {required && <span className="required">*</span>}
      </label>
      <div
        className={`upload-area ${isUploading ? 'uploading' : ''} ${isUploaded ? 'uploaded' : ''}`}
        onClick={isUploading ? null : handleClick}
      >
        {isUploading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <div className="upload-text">Processing file...</div>
          </div>
        ) : isUploaded || uploadedFileName?.fileName ? (
          <>
            <div className="upload-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#4CAF50"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <div className="upload-text">{fileName || uploadedFileName?.fileName}</div>
            <div className="upload-again" onClick={handleUploadAgain}>
              Upload Again ?
            </div>
          </>
        ) : (
          <>
            <div className="upload-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
            </div>
            <div className="upload-text">
              {fileName ? `Selected: ${fileName}` : 'Click or drag to upload a file'}
            </div>
            <div className="file-types">PDF, JPG, JPEG, PNG, HEIC up to 10MB</div>
          </>
        )}
      </div>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".pdf,.jpg,.jpeg,.png,.heic,.heif"
        onChange={handleFileChange}
      />
    </div>
  );
};

export default FileUpload;
